/* global DEBUG_LOG */

// mixpanel-specific report utils

import type {Component} from 'panel';
import queryString from 'query-string';
import range from 'lodash/range';
import isEqual from 'lodash/isEqual';
import JSURL from 'jsurl';

import type {Location} from 'common/types/globals/location';
import {UrlManager} from 'common/contexts/url-manager/types';
import {WindowUrlManager} from 'common/contexts/url-manager/window';
import {htmlEncodeString} from 'common/util';
import {isVerifiedDomain, pluralize} from 'common/util/string';
import {RelativeDateRange} from 'common/widgets/date-range-picker/models/relative-date-range';
import {Section} from 'common/components/items-menu/types';
import {ComparisonDate} from 'common/query-builder/query-block/time-comparison-menu/types';
import type {QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS, PresetDateRange} from 'common/widgets/date-range-picker/models/presets';
import type {PropertyFilter} from 'common/query-builder/cohorts-query-builder/models/builder/property-filter';
import type {Event} from 'common/query-builder/query-block/events-menu/types';

import {
  CLUSTER_NAMES,
  DownloadMode,
  INTERNAL_PROJECTS,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  PRODUCTION_DOMAINS,
  PropertyType,
  ReportName,
  ResourceType,
  TRACKING_URL_BY_CLUSTER,
} from '../constants';
import {reporter} from '../error-reporting';
import {IRON_APPS_INFO} from '../../../apps-utils';
import {isStandalone} from './env';
import {makePathFromUrlState, StateFromUrlUtils, UrlFormat} from './state-from-url';

const defaultUrlManager = new WindowUrlManager();

export type IronAppInfo = {
  appName: string;
  title: string;
  internalReportName: string;
};

export const commonReportUtils = {
  downloadLocalCSVData,
};

export const urlToIronAppInfo: {[url: string]: IronAppInfo} = {};
Object.entries(IRON_APPS_INFO).forEach(([appName, appInfo]) => (urlToIronAppInfo[appName] = {appName, ...appInfo}));

const NON_PERSISTABLE_REPORTS = new Set<ReportName>([
  ReportName.Settings,
  ReportName.Setup,
  // views is not persisted because we can't jump from
  // a project with workspaces to a project without without workspaces
  // we let django handle the correct routing
  ReportName.Views,
]);

export function getPropertyTypeForMathType(type: MATH_TYPE, propertyType?: PropertyType): PropertyType {
  if (
    propertyType !== PropertyType.Number &&
    (type === PRIMITIVE_MATH_TYPES.MOST_FREQUENT || type === PRIMITIVE_MATH_TYPES.FIRST_VALUE)
  ) {
    return PropertyType.String;
  }

  return PropertyType.Number;
}

export function isPersistableReport(): boolean {
  const reportName = getReportName();
  return Boolean(reportName) && !NON_PERSISTABLE_REPORTS.has(reportName);
}

export function isVolt(): boolean {
  return StateFromUrlUtils.parseStateFromUrl()?.urlFormat === UrlFormat.Volt;
}

export function closestReportLink(target: HTMLElement): HTMLAnchorElement {
  return target.closest(isVolt() ? `a[href^='/project/']` : `a[href^='/report/']`);
}

/**
 * Builds a report URL for navigation, accounting for volt and non-volt url schemes.
 *
 * @param endpoint - endpoint to navigate to, e.g. /insights, /settings, /boards. If null, uses current endpoint.
 * @returns - path and hash string of the report url
 */
export function getReportUrl(
  endpoint: string,
  {projectId, workspaceId}: {projectId: string | number; workspaceId?: string | number},
): string {
  const [, appName, hash = ``] = /^\/?([^#/]+)\/?(#.*)?/.exec(endpoint);

  const useVolt = isVolt() && appName in urlToIronAppInfo;
  const path = makePathFromUrlState({
    urlFormat: useVolt ? UrlFormat.Volt : UrlFormat.Django,
    projectId: projectId ? String(projectId) : ``,
    workspaceId: workspaceId ? String(workspaceId) : ``,
    appName,
  });
  // persist search for url based feature flags
  const search = window.location.search;

  return `${path}${search}${hash}`;
}

export function getEventsReportUrlForEvent({
  projectId,
  workspaceId,
  event,
  presetDateRange = PresetDateRange.Last7Days,
}: {
  projectId: string | number;
  workspaceId?: string | number;
  event: Event;
  presetDateRange?: PresetDateRange;
}) {
  const entries: QueryEventEntry[] = [
    {
      type: ResourceType.Event,
      event,
      filters: [],
    },
  ];

  return getEventsReportUrl({
    projectId,
    workspaceId,
    entries,
    dateRange: new DateRange(PRESET_DATE_RANGE_TO_DATE_RANGE_PARAMS[presetDateRange]),
    filters: [],
  });
}

export function getEventsReportUrl({
  projectId,
  workspaceId,
  entries,
  dateRange,
  filters,
}: {
  projectId: string | number;
  workspaceId?: string | number;
  entries: QueryEventEntry[];
  dateRange: DateRange;
  filters: PropertyFilter[];
}) {
  return (
    getReportUrl(`events`, {
      projectId,
      workspaceId,
    }) + `#${JSURL.stringify({entries, dateRange, filters})}`
  );
}

export function removeForcedProjectId() {
  delete window[`__mpForceProjectId`];
}
export function forceProjectId(projectId) {
  window[`__mpForceProjectId`] = projectId;
}

/**
 * fetch current project ID, either from:
 * - URL path (/project/<id>/)
 * - mp.report.globals (when not present on url)
 */
export function getProjectId(urlManager: UrlManager = null): string {
  urlManager ||= defaultUrlManager;

  // TODO temporary escape hatch here for situations where we need to force
  // a specific project ID that can't be derived by other means (e.g. design-system).
  // In the future this should be replaced by deriving project ID (and project details)
  // from a Panel Context rather than this global helper function.
  if (`__mpForceProjectId` in window) {
    return window[`__mpForceProjectId`];
  }

  // 99% of time, urls will contain projectId e.g /report/<pid>/<appName> or /report/<pid>/view/<wid>
  const urlState = StateFromUrlUtils.parseStateFromUrl(urlManager.getPath());
  if (urlState?.projectId) {
    return urlState.projectId;
  }

  if (isStandalone()) {
    // dashboard-digest urls look like https://mixpanel.com/dashboard-digest/?project_id=1495621#access_token=...
    // see https://github.com/mixpanel/analytics/blob/84bd2753540824f610613e30c82e6adbe8d4fc99/email_digests/dashboards/dashboard_digest.py#L147
    // so we need parse from url params
    return urlManager.getQueryStringParam(`project_id`) || ``;
  } else {
    // project_id provided by django template is our last resort
    // if projectId isn't in url e.g some settings urls
    // @ts-ignore
    return String(window.mp.report?.globals?.project_id || ``);
  }
}

export function getWorkspaceId(urlManager: UrlManager = null): string {
  urlManager ||= defaultUrlManager;
  const location = urlManager.getLocation();
  const urlState = StateFromUrlUtils.parseStateFromUrl(location.pathname);
  if (urlState?.workspaceId) {
    return urlState.workspaceId;
  }

  if (isStandalone()) {
    // dashboard-digest urls look like https://mixpanel.com/dashboard-digest/?project_id=1495621#access_token=...
    // see https://github.com/mixpanel/analytics/blob/84bd2753540824f610613e30c82e6adbe8d4fc99/email_digests/dashboards/dashboard_digest.py#L147
    // so we need parse from url params
    return queryString.parse(window.location.search).workspace_id || ``;
  } else {
    // workspace_id provided by django template is our last resort
    // @ts-ignore
    return String(window.mp.report?.globals?.workspace_id || ``);
  }
}

/**
 * @returns the current report name from url e.g /report/x/(insights) => ReportName.Insights
 */
export function getReportName(ironAppName?: string): ReportName {
  ironAppName = ironAppName ?? StateFromUrlUtils.parseStateFromUrl()?.appName;
  const reportName = urlToIronAppInfo[ironAppName]?.internalReportName as ReportName;

  return reportName ?? null;
}

export function getProjectSetupUrl() {
  return getReportUrl(`setup/`, {
    projectId: getProjectId(),
    workspaceId: getWorkspaceId(),
  });
}

/*
 * downloadData()
 *
 * Initiates a direct download to the browser for any HTTP method,
 * using a terrible hack of submitting a hidden form to a hidden
 * iframe target. This hack is needed because the result may be too
 * large to fit into the client's memory before triggering the download.
 *
 */
let downloadIdCounter = 1;
export function downloadData(endpoint, params, onErrorCallback: (message: string) => void = null, method = `POST`) {
  // prepare target iframe
  const exportFrameID = `download-frame-${downloadIdCounter++}`;
  const exportFrame = document.createElement(`iframe`);
  exportFrame.id = exportFrameID;
  exportFrame.name = exportFrameID;
  exportFrame.src = ``;
  exportFrame.style.display = `none`;
  document.body.appendChild(exportFrame);

  // prepare source form
  const postForm = document.createElement(`form`);
  postForm.action = endpoint;
  postForm.method = method;
  postForm.autocomplete = `off`;
  postForm.style.display = `none`;
  postForm.target = exportFrameID;
  // This is only used in jql-console and we control all the values coming in here
  // eslint-disable-next-line no-unsanitized/property
  postForm.innerHTML = Object.keys(params)
    .map((p) => `<input autocomplete="off" type="hidden" name="${p}" value="${htmlEncodeString(params[p])}"/>`)
    .join(``);

  // submit request
  document.body.appendChild(postForm);
  postForm.submit();

  if (onErrorCallback) {
    // When the download is successful the iframe doesn't emit any events. When the export fails
    // the body of the iframe is a json response that can be parsed
    exportFrame.addEventListener(
      `load`,
      () => {
        const iframeResponse = exportFrame.contentDocument.body.innerText;
        const errorMessage = `An error occurred creating export`;
        let errorDescription;

        // the body should always be a failed json payload, but guard against it being empty or otherwise malformed
        try {
          const errorResponse = JSON.parse(iframeResponse);
          reporter.error(errorMessage, errorResponse);
          [, errorDescription] = errorResponse.error.split(`desc = `);
        } catch (err) {
          reporter.error(errorMessage, iframeResponse);
        }

        onErrorCallback(errorDescription ?? errorMessage);
      },
      {
        once: true,
      },
    );
  }
}

/**
 * Downloads a file to the user's machine.
 * @param blob - representation of data to be downloaded
 * @param title - title of file to be downloaded
 * @param format - format of file to be downloaded (e.g. PDF/PNG)
 */
export function downloadBlobAsFile(blob: Blob, title: string, format?: string) {
  const blobURL = URL.createObjectURL(blob);
  // launch named download via hidden link
  const link = document.createElement(`a`);
  link.style.display = `none`;
  link.href = blobURL;
  link.download = format ? `${title}.${format.toLowerCase()}` : title;
  document.body.appendChild(link);
  link.click();
  // clean up
  URL.revokeObjectURL(blobURL);
  link.remove();
}

function downloadLocalCSVData(filename, dataStr) {
  const link = document.createElement(`a`);
  const downloadAttrSupported = `download` in link;
  link.remove();
  let blobSize = 0;
  if (downloadAttrSupported) {
    // prepare blob
    const blob = new Blob([dataStr], {type: `text/csv;charset=utf-8`});
    blobSize = blob.size;
    downloadBlobCSVData(filename, blob);
  } else {
    // Work-around for Safari. One drawback of this method is that it doesn't support setting a filename.
    // On Safari, it downloads the file with the name 'Unknown'
    window.open(`data:attachment/csv;charset=utf-8,` + encodeURIComponent(dataStr));
  }

  return {blobSize};
}

export function downloadBlobCSVData(filename: string, blob) {
  // launch named download via hidden link
  const link = document.createElement(`a`);

  const blobURL = URL.createObjectURL(blob);

  link.style.display = `none`;
  link.href = blobURL;
  link.download = filename;
  document.body.appendChild(link);
  link.click();

  // clean up
  URL.revokeObjectURL(blobURL);
  link.remove();
}

export function downloadCSVData(url) {
  // launch named download via hidden link
  const link = document.createElement(`a`);
  link.style.display = `none`;
  link.href = url;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

// TODO epurcer - replace this with a more general-purpose tool like https://www.npmjs.com/package/debug
function getLogger(level) {
  if (typeof DEBUG_LOG !== `undefined` && DEBUG_LOG) {
    /* eslint-disable no-console */
    return function () {
      console[level](...arguments); // eslint-disable-line prefer-rest-params
    };
    /* eslint-enable no-console */
  } else {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return function () {};
  }
}

interface Logger {
  [level: string]: (...args: any) => void;
}

export const debug: Logger = [`log`, `info`, `warn`, `error`, `critical`].reduce(
  (ret, level) => ({...ret, [level]: getLogger(level)}),
  {},
);

/**
 * Rate limit reporting in prod, but always log this warning in dev.
 */
export function warnDeveloper(...args: any): void {
  debug.warn(...args);
  if (Math.random() < 0.01) {
    if (args.length === 1) {
      reporter.warn(args[0]);
    } else if (args.length === 2) {
      reporter.warn(args[0], args[1]);
    } else if (args.length > 2) {
      const [message, info, ...rest] = args;
      reporter.warn(message, info, ...rest);
    }
  }
}

export function formatMaxHistoryDays(maxHistoryDays) {
  if (maxHistoryDays < 365) {
    return `${maxHistoryDays} days`;
  } else {
    const years = Math.floor(maxHistoryDays / 365);
    return `${years} ${pluralize(`year`, years)}`;
  }
}

export function getProjectClusterFromDomain(projectDomain: string): CLUSTER_NAMES {
  switch (projectDomain) {
    case PRODUCTION_DOMAINS.US:
      return CLUSTER_NAMES.MAIN_CLUSTER_NAME;
    case PRODUCTION_DOMAINS.EU:
      return CLUSTER_NAMES.EU_CLUSTER_NAME;
    default:
      reporter.error(
        new Error(
          `Project Domain (${projectDomain}) not in ${Object.keys(PRODUCTION_DOMAINS)}. ` +
            `Current URL: ${window.location.href}`,
        ),
      );
      return CLUSTER_NAMES.MAIN_CLUSTER_NAME;
  }
}

export function getBaseTrackingUrl(clusterName) {
  let endpoint = TRACKING_URL_BY_CLUSTER[CLUSTER_NAMES.MAIN_CLUSTER_NAME];
  if (!Object.keys(TRACKING_URL_BY_CLUSTER).includes(clusterName)) {
    // n.b.: mp.report.globals.project_cluster defaults to an empty string if it is not included by the view
    reporter.error(
      new Error(
        `mp.report.globals.project_cluster (${clusterName}) not in ${Object.keys(TRACKING_URL_BY_CLUSTER)}. ` +
          `Current URL: ${window.location.href}`,
      ),
    );
  } else {
    endpoint = TRACKING_URL_BY_CLUSTER[clusterName];
  }

  return endpoint;
}

export function excludeDemos<T extends {isDemo: boolean}>(orgsOrProjects: T[]): T[] {
  return orgsOrProjects.filter((orgOrProject) => !orgOrProject.isDemo);
}

export function getDownloadMode(urlManager: UrlManager = defaultUrlManager): DownloadMode {
  if (urlManager.getPath().match(/\/embed\/?$/)) {
    return DownloadMode.File;
  }
  return Number.parseInt(urlManager.getQueryStringParam(`download`), 10) || DownloadMode.None;
}

export function getDownloadDisplayPreferences(urlManager: UrlManager = defaultUrlManager): Obj {
  try {
    return JSON.parse(urlManager.getQueryStringParam(`display_preferences`)) || {};
  } catch (err) {
    return {};
  }
}

export function isPublicMode(urlManager: UrlManager = defaultUrlManager): boolean {
  return urlManager.getPath().startsWith(`/public/`);
}

export function isEmbedMode(urlManager: UrlManager = defaultUrlManager): boolean {
  return !!urlManager.getPath().match(/\/embed\/?$/);
}

export function getMpSourceFromUrl(urlManager: UrlManager = defaultUrlManager): string {
  return urlManager.getQueryStringParam(`mp_source`);
}

export function getCustomAlertIdFromUrl(urlManager: UrlManager = defaultUrlManager): string {
  return urlManager.getQueryStringParam(`alert_id`);
}

export function isDigestMode(urlManager: UrlManager = defaultUrlManager): boolean {
  return urlManager.getPath().startsWith(`/dashboard-digest`);
}

export function isTvMode(urlManager: UrlManager = defaultUrlManager): boolean {
  const tvParam = urlManager.getQueryStringParam(`tv`);
  return tvParam === `true` || tvParam === `1`;
}

export function isProduction() {
  return (Object.values(PRODUCTION_DOMAINS) as string[]).includes(window.location.hostname);
}

export function isStaffEmail(email: string): boolean {
  return !!email && isVerifiedDomain(email, [{domain: PRODUCTION_DOMAINS.US}]);
}

function getQueryStringParam(location: Location, paramName: string) {
  let paramVal = new URLSearchParams(location.search).get(paramName);
  if (paramVal === null) {
    paramVal = new URLSearchParams(location.hash).get(paramName); // wtf dashboards
  }
  return paramVal;
}

// useful for ad-hoc trials of experimental features using URL query params (can also optionally enable feature by default in internal dogfood projects)
export function isUrlDefinedExperimentalFeatureEnabled(
  location: Location,
  urlQueryParam: string,
  enableInDogfoodProjectsOrCallback: boolean | (() => boolean),
): boolean {
  const enabledInUrl = getQueryStringParam(location, urlQueryParam);
  let enabledInProjects = false;
  if (typeof enableInDogfoodProjectsOrCallback === `boolean`) {
    enabledInProjects = enableInDogfoodProjectsOrCallback ? isCurrentProjectInternal() : false;
  } else {
    enabledInProjects = enableInDogfoodProjectsOrCallback();
  }
  const disabledInUrl = [`false`, `0`].includes(getQueryStringParam(location, urlQueryParam));
  return (enabledInUrl || enabledInProjects) && !(disabledInUrl || DISABLE_EXPERIMENTAL_FEATURES);
}

export function isCurrentProjectInternal() {
  return INTERNAL_PROJECTS.has(parseInt(getProjectId(), 10));
}

export function* cycleWithSkip<T>(arr: T[], skips: T[]) {
  while (true) {
    for (const item of arr) {
      if (skips.find((i) => i === item)) {
        const itemIndex = skips.indexOf(item);
        delete skips[itemIndex];
      } else {
        yield item;
      }
    }
  }
}

/** Assigns numbers to keys, taking into account previous mappings to attempt to prevent overlap.
 * This is useful for assigning colors to items, as it will skip previously assigned numbers to ensure that the frequency
 * of the number use is minimized
 *
 * @param assignedKeys - previously assigned keys and their ordinal mappings. (their mappings frequency will be used to reduce re-using of ordinal numbers when possible
 * @param newKeys - new keys to be assigned mappings to
 * @param rangeEnd - acts like a modulo: segment keys are sequentially assigned a color index/ordinal from 1 to @maxNumber, resetting to 1 when @maxNumber is reached
 */
export function assignOrdinalNumbersToSegmentKeys(
  assignedKeys: Map<string, number>,
  newKeys: string[],
  rangeEnd: number,
): Map<string, number> {
  const skips = [];
  for (const number of assignedKeys.values()) {
    skips.push(number);
  }
  const rangeArr = range(1, rangeEnd + 1);

  const numberAssignments = new Map(assignedKeys);

  const cycle = cycleWithSkip(rangeArr, skips);

  newKeys.forEach((key) => {
    numberAssignments.set(key, cycle.next().value);
  });

  return numberAssignments;
}

// TODO(sam) move these to `lift-comparison-menu` when we deprecate the old `time-comparison-menu` in favor of lift
export function getDateRangeComparisonOptions(maxDataHistoryDays: number, matchedPreset: {label?: string}): Section[] {
  const presetOptions = RelativeDateRange.getTimeComparisonPresetOptions()
    .filter((option) => {
      const exceedsMaxHistory =
        maxDataHistoryDays &&
        !new RelativeDateRange({type: option.value.type, window: option.value.offset}).isWithinMaxHistoryLimit(
          maxDataHistoryDays,
        );
      return !exceedsMaxHistory;
    })
    .map((option) => {
      return {
        isSelected: matchedPreset?.label === option.label,
        label: option.label,
        value: option.value.offset,
      };
    });

  const customItem = {
    hasCaret: true,
    persistCaret: true,
    label: `Custom date range...`,
    value: `custom`,
  };

  return [
    {
      items: [...presetOptions, customItem],
    },
  ];
}

// TODO(sam) move these to `lift-comparison-menu` when we deprecate the old `time-comparison-menu` in favor of lift
export function getPresetMatch(comparisonDate?: ComparisonDate) {
  if (!comparisonDate?.offset) {
    return null;
  }
  return RelativeDateRange.getTimeComparisonPresetOptions().find((option) =>
    isEqual(comparisonDate, {offset: option.value.offset}),
  );
}

export function navigateDuringInitialization(appComponent: Component<any, any, any, any, any>, url: string) {
  // Navigating while the app component is initializing (e.g. connectedCallback, matched route code when loading URL)
  // doesn't work. There's a few aspects contributing to this, including the fact that `$fragment` updates don't get
  // written to the URL during panel component initialization:
  // https://github.com/mixpanel/panel/blob/3938b8dedc674cc7f838d749590e642963cf5b48/lib/component.js#L941.
  // Using a setTimeout works around this.
  setTimeout(() => appComponent.navigate(url));
}
